import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, LineElement, PointElement, TimeScale, LinearScale, Tooltip, Legend } from "chart.js";
import { useNavigate } from "react-router-dom";
import "chartjs-adapter-date-fns"; // For handling date formats
import styles from "./ChartData.module.css";

ChartJS.register(LineElement, PointElement, TimeScale, LinearScale, Tooltip, Legend);

const ChartData = ({ font, data, isCalendarVisible, setIsCalendarVisible }) => {
    const [chartDataRevenue, setChartDataRevenue] = useState(null);
    const [chartDataLabels, setChartDataLabels] = useState(null);
    const [showChartLabels, setShowChartLabels] = useState(false);
    const [showChart, setShowChart] = useState(false);
    const screenWidth = window.innerWidth; // Get the screen width in pixels
	const lineWidth = screenWidth >= 800 ? 5 : 3; // Set lineWidth based on screen width
	const chartFontSize = screenWidth >= 800 ? 12 : 8;
	const navigate = useNavigate();

	// Reload page on resize to deal with chart styling changes
    useEffect(() => {
        const handleResize = () => { navigate("/home", { state: { page: "Analytics" } }); };
        window.addEventListener("resize", handleResize); // Add the event listener
        return () => { window.removeEventListener("resize", handleResize); }; // Cleanup the event listener
    }, []);

    useEffect(() => {
        if (data && data.length) {
            setShowChart(false);
            const sortedData = [...data].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)); // Sort data by date
            let cumulativeRevenue = 0;
            let cumulativeLabels = 0;
            const labels = [];
            const revenues = [];
            const labelCounts = [];
            sortedData.forEach((shipment) => {
                cumulativeRevenue += shipment.label_purchase_price;
                cumulativeLabels += 1;
                labels.push(new Date(shipment.created_at)); // Use Date objects for x-axis
                revenues.push(cumulativeRevenue);
                labelCounts.push(cumulativeLabels);
            });
            const timeout = setTimeout(() => {
                // Set revenue chart data
                setChartDataRevenue({
                    labels,
                    datasets: [
                        {
                            data: revenues,
                            borderColor: "#6c7c9c",
                            borderWidth: lineWidth,
                            tension: 0.4, // Smooth line
                            radius: 0, // No points
                        },
                    ],
                });
                // Set labels chart data
                setChartDataLabels({
                    labels,
                    datasets: [
                        {
                            data: labelCounts,
                            borderColor: "#6c7c9c",
                            borderWidth: lineWidth,
                            tension: 0.4, // Smooth line
                            radius: 0, // No points
                        },
                    ],
                });
                setShowChart(true);
            }, 500); // Timeout for loader
            return () => clearTimeout(timeout);
        }
    }, [data]);

    // Animation settings for progressive line drawing
    const totalDuration = 1000;
    const delayBetweenPoints = totalDuration / (data ? data.length : 1);
    const animation = {
        x: {
            type: "number",
            easing: "linear",
            duration: delayBetweenPoints,
            from: NaN, // Point is initially skipped
            delay(ctx) {
                if (ctx.type !== "data" || ctx.xStarted) return 0;
                ctx.xStarted = true;
                return ctx.index * delayBetweenPoints; // Delay based on the index
            },
        },
        y: {
            type: "number",
            easing: "easeInOutQuad",
            duration: delayBetweenPoints,
            from: (ctx) =>
                ctx.index === 0
                    ? ctx.chart.scales.y.getPixelForValue(0) // Start from the y=0 axis
                    : ctx.chart
                          .getDatasetMeta(ctx.datasetIndex)
                          .data[ctx.index - 1].getProps(["y"], true).y,
            delay(ctx) {
                if (ctx.type !== "data" || ctx.yStarted) return 0;
                ctx.yStarted = true;
                return ctx.index * delayBetweenPoints;
            },
        },
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation,
        scales: {
            x: {
                type: "time",
                time: {
                    unit: "day", // Group x-axis by days
                    tooltipFormat: "MMM dd, yy", // Tooltip format
                },
                title: {
                    display: false,
                },
                grid: { display: false },
                ticks: {
                    font: { 
                    	size: chartFontSize,
                    	weight: "bold",
                    	family: "Nunito"
                    },
                    stepSize: 5,
                },
            },
            y: {
                title: {
                    display: true,
                    text: "Revenue ($)",
                },
                grid: { display: false },
                ticks: {
                    font: { 
                    	size: chartFontSize,
                    	weight: "bold",
                    	family: "Nunito" 
                    },
                },
            },
        },
        plugins: {
            legend: { display: false },
            tooltip: { enabled: true },
        },
    };
    const optionsLabels = {
        ...options,
        scales: {
            ...options.scales,
            y: {
                ...options.scales.y,
                title: {
                    display: true,
                    text: "# of Labels",
                },
            },
        },
    };

    setTimeout(() => { setShowChartLabels(true); }, 2000) 
    	
    return (
    	<>
	    	{!isCalendarVisible && (
			    <div className={`${styles.chart_data_container} ${font}`}>
                    {data?.length ? (
    			        showChart && chartDataRevenue && chartDataLabels ? (
    			            <>
    				            <div className={`${styles.chart_label_container} fade_in`}>
    				            	{showChartLabels && (<p className={`titles fade_in ${styles.chart_labels}`}>Number of Labels Sold</p>)}
    				                <div className={styles.chart_container}>
    				                    <Line data={chartDataLabels} options={optionsLabels} className="fade_in" />
    				                </div>
    				            </div>
    				            <div className={`${styles.chart_label_container} fade_in`}>
    			            		{showChartLabels && (<p className={`titles fade_in ${styles.chart_labels}`}>Label Revenue</p>)}
    				                <div className={styles.chart_container}>
    				                    <Line data={chartDataRevenue} options={options} className="fade_in" />
    				                </div>
    				            </div>
    			            </>
    			        ) : (<img className={styles.charts_loader} src={`${process.env.PUBLIC_URL}/images/shipwheel_loader.gif`} alt="loader" />)
                    ) : (<p>No data</p>)}
			    </div>
			)}
		</>
	);
};

export default ChartData;